// The real authenticated brand bar (Tailwind + i18n + RTL). Replaced the
// mockup `PaneBrand` chrome (since removed). Logo + name + sync-dot slot +
// `ms-auto` spacer + the 2nd LanguageToggle instance (F-FOUND B3) + `⋯` →
// logout menu.
//
// Logout-menu logic was ported from the old PaneBrand (outside-click + Esc close,
// useAuthStore.logout). W5: logout resets every store (wired in
// useAuth.logout). The reference implementation of CLAUDE.md §9 — logical
// utilities only (`ms-auto`, `end-0`, `text-start`), all strings via t().

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/ui/auth/useAuth'
import { LanguageToggle } from '@/ui/components/LanguageToggle'

export type SyncState = 'saved' | 'live' | 'unsaved' | 'offline'

interface BrandBarProps {
  syncState?: SyncState
}

export function BrandBar({ syncState = 'saved' }: BrandBarProps): JSX.Element {
  const { t } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const email = useAuthStore((s) => s.session?.email ?? null)
  const logout = useAuthStore((s) => s.logout)

  // Close on outside click / Esc — the taskpane is narrow, the dropdown must
  // not linger if the lawyer clicks back to the doc.
  useEffect(() => {
    if (!menuOpen) return
    function onDown(e: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  async function onLogout(): Promise<void> {
    setMenuOpen(false)
    await logout()
  }

  return (
    <div
      data-testid="shell-brand"
      className="flex items-center gap-2 bg-ink px-3 py-2 text-white"
    >
      <div className="font-serif text-lg leading-none">
        𝓅<span className="text-gold">.</span>
      </div>
      <div className="text-sm font-semibold tracking-tight">
        the<em className="not-italic text-gold">plaidoyer</em>
      </div>

      <div className="ms-auto flex items-center gap-2">
        <span
          data-testid="shell-sync"
          className="flex items-center gap-1 text-[10px] text-white/60"
        >
          <span
            aria-hidden="true"
            className={[
              'h-1.5 w-1.5 rounded-full',
              syncState === 'live'
                ? 'bg-gold'
                : syncState === 'unsaved'
                  ? 'bg-[var(--warn)]'
                  : syncState === 'offline'
                    ? 'bg-white/40'
                    : 'bg-faits',
            ].join(' ')}
          />
          {t(`shell.sync.${syncState}`)}
        </span>

        <LanguageToggle />

        <div ref={menuRef} className="relative">
          <button
            type="button"
            className="rounded px-1.5 text-base leading-none text-white/80 hover:text-white"
            title={t('shell.brand.more')}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            data-testid="shell-brand-more"
            onClick={() => setMenuOpen((o) => !o)}
          >
            ⋯
          </button>
          {menuOpen && (
            <div
              role="menu"
              data-testid="shell-brand-menu"
              className="absolute end-0 top-8 z-50 min-w-[180px] rounded-lg border border-ink/15 bg-white p-1 text-start text-xs text-ink shadow-xl"
            >
              {email && (
                <div className="mb-1 truncate border-b border-ink/10 px-2.5 py-1.5 text-[10.5px] text-ink/60">
                  {email}
                </div>
              )}
              <button
                type="button"
                role="menuitem"
                data-testid="shell-brand-logout"
                onClick={() => void onLogout()}
                className="block w-full rounded px-2.5 py-1.5 text-start text-xs font-semibold text-ink hover:bg-[var(--bg-hover)]"
              >
                {t('shell.brand.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
